import { Trans } from "@lingui/react/macro"
import { IconBulb } from "@tabler/icons-react"
import { useMemo } from "react"
import { EditableAnalysisField } from "../../../EditableAnalysisField"
import { resolveSeekTime } from "../../helpers"
import { SeekControl } from "../../parts/SeekControl"
import classes from "../../styles.module.css"
import type { IProps } from "./interfaces"

/**
 * OpenQuestionsCard renders the analyser's flat list of unanswered questions
 * as a numbered list. Each entry can carry a SeekIcon when a streaming
 * segment matches the question text by word overlap.
 */
export function OpenQuestionsCard({ questions, segments, seekOffsets, editContext }: IProps): React.JSX.Element | null {
  const seekTimes = useMemo(
    () => questions.map((q, i) => resolveSeekTime(seekOffsets?.[i], q, segments)),
    [questions, segments, seekOffsets],
  )
  if (!questions || questions.length === 0) return null
  return (
    <section className={classes.card}>
      <div className={classes.cardHead}>
        <h2 className={classes.cardTitle}>
          <IconBulb size={16} color="var(--mantine-color-info-4)" />
          <Trans>Open questions</Trans>
        </h2>
        <span className={classes.cardCount}>{questions.length}</span>
      </div>
      <ol className={classes.questionsList}>
        {questions.map((q, i) => {
          const path = `open_questions.${i}`
          return (
            <li key={`${i}-${q}`}>
              {/* Inner span keeps text + timestamp together in the grid's
                  content column — on the <li> itself the chip would become a
                  third grid item and wrap under the counter. */}
              <span className={classes.seekableInline}>
                {editContext ? (
                  <EditableAnalysisField
                    meetingId={editContext.meetingId}
                    fieldPath={path}
                    value={q}
                    canEdit={editContext.canEdit}
                    isEdited={!!editContext.fieldEdits[path]}
                    onSaved={editContext.onEdited}
                  />
                ) : (
                  q
                )}
                <SeekControl seekTime={seekTimes[i]} />
              </span>
            </li>
          )
        })}
      </ol>
    </section>
  )
}
